import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { TailSpin } from "react-loader-spinner";
import { FaStar } from "react-icons/fa";
import { IoTimeOutline } from "react-icons/io5";
import { StoreContext } from "../context/StoreContext";
import { isRestaurantClosed, sortRestaurantsByOpenStatus, getClosingSoonMessage } from "../utils/restaurantHours";

const PAGE_SIZE = 10;

const RouteSuggestions = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { setRoadId, setRequestId, userDetails, userId, accessToken, transientUser } =
    useContext(StoreContext);

  const sessionId = searchParams.get("sessionId");
  const key = searchParams.get("key");
  const useCaseId = searchParams.get("useCaseId");

  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);

  // sessionId doubles as the auth-token for this entry point, same as other QR/link flows.
  useEffect(() => {
    if (!sessionId) return;
    setRequestId(sessionId);
    userDetails(sessionId).catch(() => null);
  }, [sessionId, setRequestId, userDetails]);

  const fetchPage = useCallback(
    async (lastItemId, append) => {
      try {
        const payload = {
          useCaseId,
          key,
          lastItemId,
          pageSize: PAGE_SIZE,
          serviceInfo: {
            type: "RESTAURANT",
            filters: [],
            distanceTimeCalculator: "HAVERSINE",
          },
        };

        const response = await axios.post(
          "/api/catalogue/discovery/curated-places",
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        const raw = response?.data?.data?.data || [];
        const flattened = raw.flat().filter(Boolean);
        const nextAvailable = Boolean(response?.data?.data?.nextPageMeta?.available);

        setRestaurants((prev) =>
          sortRestaurantsByOpenStatus(append ? [...prev, ...flattened] : flattened)
        );
        setHasMore(nextAvailable);
        setError(null);
      } catch (e) {
        console.error("Failed to fetch curated restaurants:", e);
        setError("Could not load restaurants right now. Please try again.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [useCaseId, key]
  );

  useEffect(() => {
    if (!key || !useCaseId) {
      setError("This link is missing required information.");
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPage(0, false);
  }, [key, useCaseId, fetchPage]);

  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    fetchPage(restaurants.length, true);
  };

  const handleRestaurantClick = (restaurant) => {
    if (restaurant?.positionGeohash) {
      setRoadId(restaurant.positionGeohash);
    }
    try {
      if (restaurant?.placeId) {
        localStorage.setItem("supportedServeTypesRestaurantId", restaurant.placeId);
      }
      if (Array.isArray(restaurant?.supportedServeTypes)) {
        localStorage.setItem(
          "supportedServeTypes",
          JSON.stringify(restaurant.supportedServeTypes)
        );
      } else {
        localStorage.removeItem("supportedServeTypes");
      }
    } catch (e) {
      // ignore storage errors
    }

    navigate(`/menu-items/${restaurant.placeId}`, {
      state: {
        restaurantId: restaurant.placeId,
        name: restaurant.name,
        shortAddress: restaurant.shortAddress,
        rating: restaurant.rating,
        ratingCount: restaurant.ratingCount,
        opensAt: restaurant.opensAt,
        closesAt: restaurant.closesAt,
        positionGeohash: restaurant.positionGeohash,
        sessionId,
        userId,
        accessToken,
        transientUser,
        supportedServeTypes: restaurant.supportedServeTypes,
      },
    });
  };

  if (loading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <TailSpin
          height="80"
          width="80"
          color="#FBC02D"
          ariaLabel="loading"
          radius="1"
          visible={true}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-screen flex flex-col justify-center items-center px-4 text-center space-y-4">
        <h2 className="text-2xl font-semibold text-gray-800">Oops</h2>
        <p className="text-gray-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-[#FBC02D] text-white px-6 py-3 rounded-lg hover:bg-[#F9A825] transition-colors font-semibold"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="w-full h-screen flex-col justify-center pt-24 px-4">
      <h1 className="flex items-center h-12 mb-4 tracking-wide font-semibold text-lg">
        Restaurants on your route
      </h1>
      {restaurants.length === 0 && (
        <p className="text-gray-600">No restaurants found for this route.</p>
      )}
      <div className="space-y-6">
        {restaurants.map((restaurant) => {
          const closed = isRestaurantClosed(restaurant);
          const closingSoonLabel = !closed ? getClosingSoonMessage(restaurant) : null;
          return (
            <div
              key={restaurant.placeId}
              onClick={() => handleRestaurantClick(restaurant)}
              className={`flex pb-4 border-b border-gray-200 cursor-pointer ${
                closed ? "grayscale opacity-60" : ""
              }`}
            >
              <div className="w-[30%] md:w-[20%] h-full">
                {restaurant.thumbnailUrl ? (
                  <img
                    src={restaurant.thumbnailUrl}
                    alt={restaurant.name}
                    className="rounded-3xl w-full md:w-40 h-28 object-cover"
                  />
                ) : (
                  <div className="rounded-3xl w-full md:w-40 h-28 bg-gray-100 flex items-center justify-center text-gray-500 text-sm">
                    No image
                  </div>
                )}
              </div>
              <div className="w-[70%] md:w-[80%] flex flex-col justify-center pl-4 space-y-1">
                <h2 className="font-semibold text-xl">{restaurant.name}</h2>
                <div className="flex items-center text-sm text-gray-700">
                  <FaStar className="text-yellow-400 mr-1" />
                  <span className="font-bold">
                    {restaurant.rating?.toFixed?.(1) ?? restaurant.rating ?? 0}
                  </span>
                  <span className="text-gray-500 ml-2">({restaurant.ratingCount || 0})</span>
                </div>
                <p className="text-sm text-gray-600">
                  {restaurant.tags?.length ? restaurant.tags.join(", ") : "Restaurant"}
                </p>
                <p className="text-sm text-gray-600">{restaurant.shortAddress || "Nearby"}</p>
                {closingSoonLabel && (
                  <span className="inline-flex items-center gap-1 w-fit text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                    <IoTimeOutline className="h-3.5 w-3.5" />
                    {closingSoonLabel}
                  </span>
                )}
                {restaurant.thumbnailFooterTexts?.length > 0 && (
                  <div className="flex items-center gap-2">
                    {restaurant.thumbnailFooterTexts.map((badge, idx) => (
                      <span
                        key={idx}
                        className={
                          badge.bold
                            ? "text-xs font-bold text-green-700"
                            : "text-xs text-gray-500"
                        }
                      >
                        {badge.text}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {hasMore && (
        <div className="flex justify-center py-6">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className={`bg-[#FBC02D] text-white px-6 py-3 rounded-lg font-semibold transition-colors ${
              loadingMore ? "opacity-70 cursor-not-allowed" : "hover:bg-[#F9A825]"
            }`}
          >
            {loadingMore ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
};

export default RouteSuggestions;
